/* ========================
 * PruningSlider.jsx
 * Smart context management with real-time feedback and global defaults
 * Shows live updates as you drag and provides advanced settings
 * ======================== */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Scissors, Thermometer, Lock, FileText, Image as ImageIcon, XCircle, Settings, Trash2 } from 'lucide-react';
import { botManager } from '@/managers/BotManager';
import { useConversation } from '@/managers/ConvoManager';
import { PaidGate } from '@/components/PaidGate';
import { authManager } from '@/managers/AuthManager';
import { pinManager } from '@/components/tools/pinned/PinManager';
import { 
  estimateMessageTokens, 
  analyzeConversationFiles, 
  getGlobalMemoryDefaults, 
  updateGlobalMemoryDefaults 
} from '@/managers/ConvoManager/ConvoPruning';
import { useTheme } from '@/managers/ThemeProvider';

/* ========================
 * useBot - Custom hook to manage bot state and subscriptions
 * ======================== */
const useBot = () => {
  const [botState, setBotState] = useState(botManager.getState());

  useEffect(() => {
    return botManager.subscribe(setBotState);
  }, []);

  return {
    currentBot: botState.currentBot,
    currentVariation: botState.currentVariation,
    isLoading: botState.isLoading
  };
};

/* ========================
 * GlobalDefaultsMenu - Advanced settings menu for memory management
 * ======================== */
const GlobalDefaultsMenu = ({ isOpen, onClose, currentMemorySize }) => {
  const { isLightTheme } = useTheme();
  const [defaults, setDefaults] = useState(getGlobalMemoryDefaults());
  const [hasChanges, setHasChanges] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDefaults(getGlobalMemoryDefaults());
      setHasChanges(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleDefaultChange = (field, value) => {
    setDefaults(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateGlobalMemoryDefaults(defaults);
    setHasChanges(false);
    onClose();
  };

  const handleReset = () => {
    setDefaults({ defaultMemorySize: 20 });
    setHasChanges(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[10000]">
      <div 
        ref={menuRef}
        className={`p-6 rounded-lg shadow-xl border max-w-md w-full mx-4 bg-surface border-border-color`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-on-surface">Memory Defaults</h3>
          <button onClick={onClose} className="text-on-surface-dim hover:text-on-surface">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-on-surface mb-2">
              Default Memory Size
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="range"
                min="5"
                max="100"
                value={defaults.defaultMemorySize}
                onChange={(e) => handleDefaultChange('defaultMemorySize', parseInt(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-medium text-primary w-8 text-right">
                {defaults.defaultMemorySize}
              </span>
            </div>
            <p className="text-xs text-on-surface-dim mt-1">
              Starting memory size for new conversations
            </p>
          </div>

          <div className={`p-3 rounded-md ${isLightTheme ? 'bg-background-dark/50' : 'bg-surface-dark/30'}`}>
            <div className="text-xs text-on-surface-dim space-y-1">
              <div><strong>Current conversation:</strong> {currentMemorySize} messages</div>
              <div><strong>Your default:</strong> {defaults.defaultMemorySize} messages</div>
              <div><strong>Maximum allowed:</strong> 100 messages</div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border-color">
          <button onClick={handleReset} className="text-sm text-on-surface-dim hover:text-on-surface">
            Reset to 20
          </button>
          <div className="flex space-x-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-on-surface-dim hover:text-on-surface">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={`px-3 py-1.5 text-sm rounded-md
                ${hasChanges 
                  ? 'bg-primary text-white hover:bg-primary-dark' 
                  : 'bg-surface-dark text-on-surface-dim cursor-not-allowed'
                }`}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ========================
 * PruningSlider - Smart context management with real-time feedback
 * ======================== */
export const PruningSlider = ({ isOpen, onToggle, sliderRef }) => {
  const { isLightTheme } = useTheme();
  const { currentVariation } = useBot();
  const {
    currentConversation,
    updateTemperature,
    updateAIMemorySize,
    loadMoreMessagesIfNeeded,
    inputEstimation,
    removeAttachment,
    removeAllAttachments,
  } = useConversation();

  const userTier = authManager.getState().tier;
  const [pinState, setPinState] = useState(pinManager.getState());
  
  const [localMemorySize, setLocalMemorySize] = useState(15);
  const [temperatureValue, setTemperatureValue] = useState(0.7);
  const [isDragging, setIsDragging] = useState(false);
  const [removingFileId, setRemovingFileId] = useState(null);
  const [isRemovingAll, setIsRemovingAll] = useState(false);
  const [showGlobalDefaults, setShowGlobalDefaults] = useState(false);
  const [globalDefaults, setGlobalDefaults] = useState(getGlobalMemoryDefaults());

  useEffect(() => {
    const updateDefaults = () => setGlobalDefaults(getGlobalMemoryDefaults());
    window.addEventListener('storage', updateDefaults);
    return () => window.removeEventListener('storage', updateDefaults);
  }, []);

  const getEffectiveMemorySize = useCallback(() => {
    if (!currentConversation) return userTier === 'Free' ? 10 : globalDefaults.defaultMemorySize;
    const tier = authManager.getState().tier;
    const defaultSize = tier === 'Free' ? 10 : globalDefaults.defaultMemorySize;
    return Math.min(100, Math.max(5, currentConversation?.memorySize ?? defaultSize));
  }, [currentConversation?.memorySize, currentConversation?.id, userTier, globalDefaults.defaultMemorySize]);

  useEffect(() => {
    return pinManager.subscribe(setPinState);
  }, []);

  useEffect(() => {
    setLocalMemorySize(getEffectiveMemorySize());
  }, [getEffectiveMemorySize]);

  useEffect(() => {
    if (currentConversation?.temperature !== undefined) {
      setTemperatureValue(currentConversation.temperature);
    }
  }, [currentConversation?.temperature]);

  // Get valid messages from conversation
  const allValidMessagesInState = useMemo(() => {
    if (!currentConversation?.messages) return [];
    return currentConversation.messages.filter(msg => !msg.isError && !msg.isStatusMessage);
  }, [currentConversation?.messages]);

  const messagesLoadedCount = allValidMessagesInState.length;

  // Get messages within the memory window
  const messagesWithinMemoryWindow = useMemo(() => {
    return allValidMessagesInState.slice(-localMemorySize);
  }, [allValidMessagesInState, localMemorySize]);

  // SIMPLIFIED: Derive analyzed files directly from messages - no local state needed
  const analyzedFiles = useMemo(() => {
    if (!currentConversation?.messages) return [];
    
    const messagesInWindowIds = new Set(messagesWithinMemoryWindow.map(msg => msg.id));
    return analyzeConversationFiles(allValidMessagesInState, messagesInWindowIds);
  }, [currentConversation?.messages, allValidMessagesInState, messagesWithinMemoryWindow]);

  // Handle outside clicks to close
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (sliderRef?.current && !sliderRef.current.contains(event.target)) {
        if (isOpen) onToggle();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen, onToggle, sliderRef]);

  // Calculate tokens and message metrics
  const currentMetrics = useMemo(() => {
    const safeInputEstimation = inputEstimation || { estimatedTokens: 0, textTokens: 0, fileTokens: 0, fileCount: 0 };

    if (!currentConversation?.messages) {
      return {
        tokens: { input: 0, output: 0 },
        validMessageCountInWindow: 0,
        totalMessagesInState: 0,
        totalContextSize: safeInputEstimation.estimatedTokens,
        historyTokensInWindow: 0,
        nextMessageEstimate: safeInputEstimation.estimatedTokens,
        maxAvailableMessagesInState: 0,
        pinnedCount: 0,
        hasManifest: false,
        textTokens: safeInputEstimation.textTokens,
        fileTokens: safeInputEstimation.fileTokens,
        fileCount: safeInputEstimation.fileCount
      };
    }

    const activePins = pinState.pins.filter(pin => pinManager.isActive(pin.id));
    let historyTokenCountInWindow = 0;
    const estimatedSystemPromptTokens = 1500;

    messagesWithinMemoryWindow.forEach(msg => {
      historyTokenCountInWindow += estimateMessageTokens(msg);
    });

    historyTokenCountInWindow += estimatedSystemPromptTokens;

    activePins.forEach(pin => {
      if (pin.content) historyTokenCountInWindow += Math.ceil((pin.content || '').length / 3.5);
    });

    const totalContextSize = historyTokenCountInWindow + safeInputEstimation.estimatedTokens;

    return {
      tokens: { input: historyTokenCountInWindow, output: 0 },
      validMessageCountInWindow: messagesWithinMemoryWindow.length,
      totalMessagesInState: allValidMessagesInState.length,
      maxAvailableMessagesInState: allValidMessagesInState.length,
      pinnedCount: activePins.length,
      hasManifest: false,
      totalContextSize,
      historyTokensInWindow: historyTokenCountInWindow,
      nextMessageEstimate: safeInputEstimation.estimatedTokens,
      textTokens: safeInputEstimation.textTokens,
      fileTokens: safeInputEstimation.fileTokens,
      fileCount: safeInputEstimation.fileCount
    };
  }, [messagesWithinMemoryWindow, allValidMessagesInState.length, inputEstimation, pinState.pins, currentConversation?.messages]);

  // Handlers
  const handleMemoryDrag = (e) => {
    if (userTier === 'Free') return;
    setLocalMemorySize(Math.max(5, Math.min(100, Number(e.target.value))));
    setIsDragging(true);
  };

  const handleMemoryDragEnd = () => {
    if (userTier === 'Free') return;
    setIsDragging(false);
    if (currentConversation) {
      updateAIMemorySize(localMemorySize);
      if (localMemorySize > messagesLoadedCount) {
        loadMoreMessagesIfNeeded(currentConversation.id, localMemorySize);
      }
    }
  };

  const handleTempChange = (e) => setTemperatureValue(parseFloat(e.target.value));

  const handleTempRelease = (e) => {
    if (currentConversation) {
      updateTemperature(parseFloat(e.target.value));
    }
  };

  // SIMPLIFIED: Just call removeAttachment - state update triggers UI refresh automatically
  const handleRemoveFile = async (attachmentId, messageId, type) => {
    if (!removeAttachment || !currentConversation) return;
    
    setRemovingFileId(attachmentId);
    try {
      await removeAttachment(currentConversation.id, messageId, attachmentId, type);
    } catch (error) {
      console.error("Failed to remove attachment:", error);
    } finally {
      setRemovingFileId(null);
    }
  };

  // SIMPLIFIED: Just call removeAllAttachments - state update triggers UI refresh automatically
  const handleRemoveAllAttachments = async () => {
    if (!currentConversation || analyzedFiles.length === 0) return;
    
    if (!window.confirm(`Remove all ${analyzedFiles.length} attachment(s) from this conversation? This cannot be undone.`)) {
      return;
    }
    
    setIsRemovingAll(true);
    try {
      if (removeAllAttachments) {
        await removeAllAttachments(currentConversation.id);
      } else {
        // Fallback: remove one by one
        for (const file of analyzedFiles) {
          await removeAttachment(currentConversation.id, file.messageId, file.attachmentId, file.type);
        }
      }
    } catch (error) {
      console.error("Failed to remove all attachments:", error);
    } finally {
      setIsRemovingAll(false);
    }
  };

  // Helper functions
  const getSweetSpotGuidance = (totalTokenCount) => {
    if (totalTokenCount <= 75000) return { text: "Optimal Range", tokens: "<75k", className: isLightTheme ? "text-green-600" : "text-green-400", desc: "🎯" };
    if (totalTokenCount <= 100000) return { text: "Good Balance", tokens: "<100k", className: "text-primary", desc: "✅" };
    if (totalTokenCount <= 125000) return { text: "Getting Heavy", tokens: "<125k", className: isLightTheme ? "text-yellow-600" : "text-yellow-400", desc: "⚠️" };
    return { text: "Very Heavy", tokens: ">125k", className: isLightTheme ? "text-red-600" : "text-red-400", desc: "🔴" };
  };

  const formatTokenCount = (tokenCount) => {
    if (tokenCount >= 1000) return `${(tokenCount / 1000).toFixed(1)}k`;
    return tokenCount.toString();
  };

  const Badge = ({ children, className = "" }) => (
    <div className={`inline-flex items-center px-2 py-0.5 rounded ${isLightTheme ? 'bg-background-dark' : 'bg-surface-dark/50'} text-xs font-medium ${className}`}>
      {children}
    </div>
  );

  // Render empty state
  if (!currentConversation) {
    return (
      <div className="relative" ref={sliderRef}>
        <button 
          onClick={onToggle} 
          className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-150 
            ${isLightTheme ? 'hover:bg-white/20 text-white' : 'hover:bg-surface-dark text-on-surface'} 
            ${isOpen ? (isLightTheme ? 'bg-white/30' : 'bg-surface-dark') : ''}`} 
          aria-label="Smart context management"
        >
          <Scissors className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const sliderMin = 5;
  const sliderMax = 100;
  const guidance = getSweetSpotGuidance(currentMetrics.totalContextSize);

  return (
    <>
      <div className="relative" ref={sliderRef}>
        <button
          onClick={onToggle}
          className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-150
            ${isLightTheme ? 'hover:bg-white/20 text-white' : 'hover:bg-surface-dark text-on-surface'}
            ${isOpen ? (isLightTheme ? 'bg-white/30' : 'bg-surface-dark') : ''}`}
          aria-label="Smart context management"
        >
          <Scissors className="w-4 h-4" />
        </button>

        {isOpen && (
          <div className={`absolute right-0 top-full mt-2 p-4 border rounded-md shadow-lg z-[9999] min-w-[280px] max-w-xs bg-surface border-border-color`}>
            <PaidGate requiredTier="Pro" feature="Smart Context Management" info="Intelligent conversation memory with optimal token management. Available for Pro users." showUpgradeButton={true} initialOverlayOpacity="0.4" hoverOverlayOpacity="0.60" buttonTopMargin="30px">
              <div className="space-y-4 mb-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <h3 className="text-sm font-medium text-on-surface">LIVE CONTEXT WINDOW</h3>
                    <button
                      onClick={() => setShowGlobalDefaults(true)}
                      className={`w-4 h-4 flex items-center justify-center rounded transition-colors duration-150
                        ${isLightTheme ? 'hover:bg-white/20 text-on-surface-dim hover:text-on-surface' : 'hover:bg-surface-dark text-on-surface-dim hover:text-on-surface'}`}
                      title="Memory defaults & advanced settings"
                    >
                      <Settings className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col items-center justify-center h-12 text-[10px] leading-tight text-on-surface-dim">
                      <div className="w-2 h-2 bg-primary rounded-sm mb-1"></div>
                      <span>Within</span>
                      <span>Context</span>
                    </div>
                    <div className={`px-3 py-1.5 rounded-md text-center ${isLightTheme ? 'bg-background-dark' : 'bg-surface-dark/50'}`}>
                      <span className={`text-lg font-medium ${isLightTheme ? 'text-green-600' : 'text-green-400'}`}>
                        {currentMetrics.validMessageCountInWindow}
                      </span>
                      <div className="text-xs text-on-surface">
                        Messages
                        {messagesLoadedCount > currentMetrics.validMessageCountInWindow && (
                          <span className="text-xs text-on-surface-dim ml-1">of {messagesLoadedCount}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center h-12 text-[10px] leading-tight text-on-surface-dim">
                      <div className={`w-2 h-2 rounded-sm mb-1 ${isLightTheme ? 'bg-red-500' : 'bg-red-400/80'}`}></div>
                      <span>Outside</span>
                      <span>Context</span>
                    </div>
                  </div>
                  <div className="relative">
                    <div className={`relative h-4 rounded-full overflow-hidden ${isLightTheme ? 'bg-background-dark' : 'bg-surface-dark'}`}>
                      {sliderMax > sliderMin && messagesLoadedCount > currentMetrics.validMessageCountInWindow && (
                        <div
                          className={`absolute top-0 right-0 h-full shadow-sm ${isLightTheme ? 'bg-red-500' : 'bg-red-400/80'}`}
                          style={{ width: `${((messagesLoadedCount - currentMetrics.validMessageCountInWindow) / sliderMax) * 100}%` }}
                        />
                      )}
                      <div
                        className={`absolute left-0 top-0 h-full transition-all duration-150 ${isDragging ? 'bg-primary-light' : 'bg-primary'}`}
                        style={{ width: `${((localMemorySize - sliderMin) / (sliderMax - sliderMin)) * 100}%` }}
                      />
                      <div
                        className={`absolute top-1/2 w-6 h-6 rounded-full border-2 border-white shadow-lg transform -translate-y-1/2 transition-all duration-150 ${isDragging ? 'bg-primary-light scale-110' : 'bg-primary'}`}
                        style={{ left: `calc(${((localMemorySize - sliderMin) / (sliderMax - sliderMin)) * 100}% - 12px)` }}
                      />
                    </div>
                    <input 
                      type="range" 
                      min={sliderMin} 
                      max={sliderMax} 
                      step="1" 
                      value={localMemorySize} 
                      onChange={handleMemoryDrag} 
                      onMouseUp={handleMemoryDragEnd} 
                      onTouchEnd={handleMemoryDragEnd} 
                      className={`absolute inset-0 w-full h-4 opacity-0 cursor-pointer ${userTier === 'Free' ? 'cursor-not-allowed' : ''}`} 
                      disabled={userTier === 'Free'} 
                    />
                    {userTier === 'Free' && (
                      <div className="absolute inset-0 flex items-center">
                        <div className="relative flex items-center w-full">
                          <div 
                            className={`absolute z-20 flex items-center justify-center w-6 h-6 border-2 rounded-full bg-surface border-border-color`} 
                            style={{ left: `calc(${((10 - sliderMin) / (sliderMax - sliderMin)) * 100}% - 12px)`, top: '50%', transform: 'translateY(-50%)' }}
                          >
                            <Lock className="w-3 h-3 text-on-surface-dim" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-[9px] text-on-surface-dim/60 mb-0.5">CURRENT STATUS</div>
                    <div className="text-xs mb-1">
                      <span className={guidance.className}>{guidance.desc} {guidance.text} ({guidance.tokens} tokens)</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs text-on-surface-dim">
                      <div><div className="font-medium">Thread</div><div className="text-primary">{formatTokenCount(currentMetrics.historyTokensInWindow)}</div></div>
                      <div><div className="font-medium">Next Msg</div><div className="text-primary">{formatTokenCount(currentMetrics.nextMessageEstimate)}</div></div>
                      <div><div className="font-medium">Total</div><div className="text-primary">{formatTokenCount(currentMetrics.totalContextSize)}</div></div>
                    </div>
                  </div>
                  {(currentMetrics.pinnedCount > 0 || currentMetrics.hasManifest) && (
                    <div className="flex justify-center gap-3 text-xs">
                      {currentMetrics.pinnedCount > 0 && <Badge className={isLightTheme ? "text-blue-600" : "text-blue-400"}>+{currentMetrics.pinnedCount} pinned</Badge>}
                      {currentMetrics.hasManifest && <Badge className={isLightTheme ? "text-green-600" : "text-green-400"}>+project</Badge>}
                    </div>
                  )}
                </div>
              </div>
            </PaidGate>

            {/* Temperature Section */}
            <div className="space-y-3 pt-4 border-t border-border-color">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-on-surface-dim">BOT TEMPERATURE</span>
                <Thermometer className="w-4 h-4 text-primary-light" />
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <div className={`relative h-2 rounded-full ${isLightTheme ? 'bg-background-dark' : 'bg-surface-dark'}`}>
                    <div className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 to-red-500 rounded-full transition-all duration-200" style={{ width: `${temperatureValue * 100}%` }} />
                  </div>
                  <input 
                    type="range" 
                    min="0" 
                    max="1" 
                    step="0.1" 
                    value={temperatureValue} 
                    onChange={handleTempChange} 
                    onMouseUp={handleTempRelease} 
                    onTouchEnd={handleTempRelease} 
                    className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer" 
                  />
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-on-surface-dim">More Focused</span>
                  <Badge className="text-primary">{temperatureValue.toFixed(1)}</Badge>
                  <span className="text-on-surface-dim">More Random</span>
                </div>
              </div>
            </div>

            {/* Files Section */}
            <div className="space-y-3 pt-4 border-t border-border-color">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-on-surface-dim">ATTACHED FILES</span>
                {analyzedFiles.length > 0 && (
                  <button
                    onClick={handleRemoveAllAttachments}
                    disabled={isRemovingAll}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors
                      ${isRemovingAll ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-500/20 text-red-500'}`}
                    title="Remove all attachments from this conversation"
                  >
                    {isRemovingAll ? (
                      <div className="w-3 h-3 border-2 border-transparent border-t-red-500 border-r-red-500 rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="w-3 h-3" />
                    )}
                    <span>Clear All ({analyzedFiles.length})</span>
                  </button>
                )}
              </div>
              {analyzedFiles.length === 0 ? (
                <p className="text-xs text-on-surface-dim text-center py-2">No files in this conversation.</p>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                  {analyzedFiles.map(file => (
                    <div key={file.attachmentId} className={`flex items-center justify-between p-2 rounded ${isLightTheme ? 'bg-background-dark/50' : 'bg-surface-dark/30'}`}>
                      <div className="flex items-center overflow-hidden mr-2">
                        {file.type === 'image' ? 
                          <ImageIcon className={`w-4 h-4 mr-2 flex-shrink-0 ${isLightTheme ? 'text-blue-600' : 'text-blue-400'}`} /> : 
                          <FileText className={`w-4 h-4 mr-2 flex-shrink-0 ${isLightTheme ? 'text-green-600' : 'text-green-400'}`} />
                        }
                        <div className="flex flex-col overflow-hidden">
                          <span className="text-xs text-on-surface truncate" title={file.name}>{file.name}</span>
                          <span className={`text-[10px] ${file.isInContext ? (isLightTheme ? 'text-green-600' : 'text-green-400') : (isLightTheme ? 'text-yellow-600' : 'text-yellow-500')}`}>
                            {file.isInContext ? 'In Context' : 'Out of Context'} <span className="text-on-surface-dim/70">{file.relativePositionText}</span>
                          </span>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleRemoveFile(file.attachmentId, file.messageId, file.type)} 
                        disabled={removingFileId === file.attachmentId || isRemovingAll} 
                        className={`p-1 rounded disabled:opacity-50 flex-shrink-0 hover:bg-red-500/20`} 
                        title="Drop this file"
                      >
                        {removingFileId === file.attachmentId ? (
                          <div className="w-3.5 h-3.5 border-2 border-transparent border-t-red-500 border-r-red-500 rounded-full animate-spin" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-red-500" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <GlobalDefaultsMenu
        isOpen={showGlobalDefaults}
        onClose={() => setShowGlobalDefaults(false)}
        currentMemorySize={localMemorySize}
      />
    </>
  );
};

export default PruningSlider;